/**
 * DashboardSkeleton Component
 * Loading skeleton for the dashboard
 */

import React from "react";
import { Box, Grid, Paper, Skeleton, Typography } from "@mui/material";

/**
 * Skeleton for a single metric card
 */
function MetricCardSkeleton() {
    return (
        <Paper
            sx={{
                p: 2.5,
                borderRadius: "12px",
                height: "100%"
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mb: 2
                }}
            >
                <Skeleton variant="rounded" width={32} height={32} />
                <Skeleton variant="text" width={100} height={20} />
            </Box>
            <Skeleton variant="text" width="60%" height={40} />
        </Paper>
    );
}

/**
 * Skeleton for a section of metric cards
 */
function SectionSkeleton({ title, cardCount = 6 }) {
    return (
        <Paper sx={{ p: 3, mb: 3, borderRadius: "12px" }}>
            <Skeleton
                variant="text"
                width={150}
                height={32}
                sx={{ mb: 2 }}
            />
            <Grid container spacing={2}>
                {Array.from({ length: cardCount }).map((_, index) => (
                    <Grid item xs={12} sm={6} md={4} key={index}>
                        <MetricCardSkeleton />
                    </Grid>
                ))}
            </Grid>
        </Paper>
    );
}

/**
 * Skeleton for the comparison chart
 */
function ChartSkeleton() {
    return (
        <Box sx={{ mt: 3 }}>
            <Skeleton
                variant="text"
                width={200}
                height={28}
                sx={{ mb: 2 }}
            />
            <Skeleton
                variant="rounded"
                width="100%"
                height={400}
                sx={{ borderRadius: "8px" }}
            />
        </Box>
    );
}

/**
 * Full dashboard loading skeleton
 */
function DashboardSkeleton() {
    return (
        <Box sx={{ p: 3 }}>
            {/* Header */}
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 3
                }}
            >
                <Skeleton variant="text" width={150} height={40} />
                <Box sx={{ display: "flex", gap: 1 }}>
                    <Skeleton variant="circular" width={40} height={40} />
                    <Skeleton variant="circular" width={40} height={40} />
                </Box>
            </Box>

            {/* Today Section */}
            <SectionSkeleton title="Today" cardCount={6} />

            {/* Current Month Section */}
            <SectionSkeleton title="Current Month" cardCount={6} />

            {/* Year Comparison Section */}
            <Paper sx={{ p: 3, borderRadius: "12px" }}>
                <Skeleton
                    variant="text"
                    width={180}
                    height={32}
                    sx={{ mb: 2 }}
                />

                {/* Summary Cards */}
                <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={12} md={6}>
                        <Paper
                            sx={{
                                p: 2,
                                backgroundColor: "grey.50",
                                borderRadius: "8px"
                            }}
                        >
                            <Skeleton
                                variant="text"
                                width={120}
                                height={24}
                                sx={{ mb: 1 }}
                            />
                            <Skeleton variant="text" width="80%" height={32} />
                            <Skeleton variant="text" width="60%" height={24} />
                        </Paper>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Paper
                            sx={{
                                p: 2,
                                backgroundColor: "grey.50",
                                borderRadius: "8px"
                            }}
                        >
                            <Skeleton
                                variant="text"
                                width={120}
                                height={24}
                                sx={{ mb: 1 }}
                            />
                            <Skeleton variant="text" width="80%" height={32} />
                            <Skeleton variant="text" width="60%" height={24} />
                        </Paper>
                    </Grid>
                </Grid>

                {/* Chart */}
                <ChartSkeleton />
            </Paper>
        </Box>
    );
}

export default DashboardSkeleton;
